import {
  Avatar,
  Box,
  FixedWidthLayout,
  IconButton,
  Spinner,
  Stack,
  TwoColumnLayout,
  Typography,
} from '@garner-health/components-common';
import { useStringResource } from '@garner-health/lib-ui-content-management';
import { useFlags } from '@garner-health/lib-ui-feature-flags';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FacilityIcon from 'jsx:~/assets/images/facility.svg';
import Physician from 'jsx:~/assets/images/physician.svg';
import React, { useEffect, useReducer, useState } from 'react';
import { NavigateFunction, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { providerClient, ProviderDetailParams, ProviderFailureReason } from '~/clients';
import { RateLimited } from '~/components/common';
import { ProfessionalDetails } from '~/components/provider-details';
import { FacilityDetails } from '~/components/provider-details/facility-details';
import { ShareProviderCard } from '~/components/provider-details/share-provider-card';
import { useScopeInfo, useSpecialtyLabel } from '~/contexts';
import {
  ProviderDetailContext,
  ProviderDetailDispatchContext,
  providerDetailsReducer,
  useProviderDetails,
} from '~/contexts/provider-detail-context';
import { useGoBack, useRumLoadVital } from '~/hooks';
import logger from '~/logging';
import { routes } from '~/router/routes';
import { getProfessionalName } from '~/util';

const log = logger(__filename);

function getImageSrc(origin: string, providerId: string) {
  return `${origin}/static/providers/profiles/${providerId}-70@3x.jpg`;
}

type MainContentParams = {
  metricIds: string[];
  searchParams: ProviderDetailParams;
};

const MainContent = ({ searchParams, metricIds }: MainContentParams) => {
  const { showProviderAffiliations } = useFlags();
  const specialtyLabel = useSpecialtyLabel()(searchParams.specialty);
  const providerAvatarAltText = useStringResource('providerDetails', 'providerAvatarAltText');
  const facilityText = useStringResource('providerDetails', 'facilityText');
  const professionalText = useStringResource('providerDetails', 'physicianText');
  const details = useProviderDetails();
  if (!details) return;
  const { professional, facility } = details;

  let providerName;
  let providerId;
  let fallback;

  let healthSystemName: string | undefined;

  if (professional) {
    providerName = getProfessionalName(professional);
    providerId = professional.id;
    fallback = <Physician role="img" aria-label={professionalText} />;
    const currentLocation = professional.locations.get(details.locationId);
    healthSystemName = showProviderAffiliations ? currentLocation?.providerCorporationName : undefined;
  } else {
    providerName = facility.organizationName;
    providerId = facility.id;
    fallback = <FacilityIcon role="img" aria-label={facilityText} />;
  }

  return (
    <>
      <Stack direction="row" spacing="md">
        <Avatar size="xl" content={{ src: getImageSrc(location.origin, providerId), alt: providerAvatarAltText }}>
          {fallback}
        </Avatar>
        <Stack justifyContent="center">
          <Typography variant="h1" fontWeight="medium">
            {providerName}
          </Typography>
          {!!specialtyLabel && (
            <Typography variant="h2" fontWeight="regular" color="common.muted">
              {specialtyLabel}
            </Typography>
          )}
          {!!healthSystemName && (
            <Typography variant="body1" color="common.muted">
              {healthSystemName}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Box paddingTop="xl">
        <TwoColumnLayout type="2:1">
          <TwoColumnLayout.Column1>
            {professional ? <ProfessionalDetails metricIds={metricIds} /> : <FacilityDetails />}
          </TwoColumnLayout.Column1>
          <TwoColumnLayout.Column2>
            <ShareProviderCard />
          </TwoColumnLayout.Column2>
        </TwoColumnLayout>
      </Box>
    </>
  );
};
type FetchParams = {
  providerId: string;
  searchParams: ProviderDetailParams;
  isDemo: boolean;
  navigate: NavigateFunction;
  setLoading: (loading: boolean) => void;
  onRateLimited: () => void;
};
function fetchProfessional({ providerId, searchParams, isDemo, navigate, setLoading, onRateLimited }: FetchParams) {
  return providerClient
    .getProfessional({ providerId, networkId: searchParams.networkId, isDemo })
    .then(response => {
      if (!response.success) {
        handleFailureReason(response.reason, { navigate, onRateLimited });
        return { professional: undefined, locationId: searchParams.locationId };
      } else {
        const locationId = response.data.locations.get(searchParams.locationId)
          ? searchParams.locationId
          : response.data.locations.keys().next().value;
        const currentSpecialty = response.data.specialties.get(searchParams.specialty)
          ? searchParams.specialty
          : response.data.specialties.keys().next().value;
        return { professional: response.data, locationId, currentSpecialty };
      }
    })
    .finally(() => setLoading(false));
}
function fetchFacility({ providerId, searchParams, isDemo, navigate, setLoading, onRateLimited }: FetchParams) {
  return providerClient
    .getFacility({ providerId, locationId: searchParams.locationId, networkId: searchParams.networkId, isDemo })
    .then(response => {
      if (!response.success) {
        handleFailureReason(response.reason, { navigate, onRateLimited });
        return { facility: undefined };
      } else {
        const currentSpecialty = response.data.specialties.get(searchParams.specialty)
          ? searchParams.specialty
          : response.data.specialties.keys().next().value;
        return { facility: response.data, currentSpecialty };
      }
    })
    .finally(() => {
      setLoading(false);
    });
}
function handleFailureReason(
  reason: ProviderFailureReason,
  { navigate, onRateLimited }: { navigate: NavigateFunction; onRateLimited: () => void },
) {
  const failureReasonHandlerMap: Record<ProviderFailureReason, () => void> = {
    notFound: () => navigate(routes.notFound()),
    // Stay on the page and swap in the message: navigating away would lose the provider the user
    // was opening, and they only need to wait.
    rateLimited: onRateLimited,
  };
  failureReasonHandlerMap[reason]();
}

export const ProviderDetailsPage = () => {
  const [details, dispatch] = useReducer(providerDetailsReducer, {});
  const [loading, setLoading] = useState(true);
  const [rateLimited, setRateLimited] = useState(false);
  const navigate = useNavigate();
  const goBack = useGoBack();
  const scopeInfo = useScopeInfo();
  if (!scopeInfo) throw new Error('No scope info in app state');
  const { isDemo } = scopeInfo;

  useRumLoadVital('provider_details_loaded', loading);

  const params = useParams();
  const [queryParams] = useSearchParams();

  const providerId = params.id!;
  const metricIds = queryParams.getAll('metricId');
  const specialty = queryParams.get('specialty')!;
  const locationId = queryParams.get('locationId')!;
  const networkId = queryParams.get('networkId')!;
  const searchParams = { locationId, specialty, networkId };

  useEffect(() => {
    if (!providerId || !locationId || !specialty || !networkId) {
      log.error(
        { providerId, locationId, networkId, strSpecialty: specialty, metricId: metricIds },
        'Missing required params',
      );
      navigate(routes.notFound());
      return;
    }

    setLoading(true);
    /**
     * Function to handle fetching a professional, and updating provider details context with new data
     */
    function handleGetProfessional() {
      fetchProfessional({
        providerId,
        searchParams,
        isDemo,
        navigate,
        setLoading,
        onRateLimited: () => setRateLimited(true),
      })
        .then(({ professional, locationId: professionalLocationId, currentSpecialty }) => {
          if (professional) {
            dispatch({
              type: 'updateProfessional',
              professional,
              locationId: professionalLocationId,
              specialty: currentSpecialty,
              networkId: searchParams.networkId,
            });
          }
        })
        .finally(() => setLoading(false));
    }

    /**
     * Function to handle fetching a facility, and updating provider details context with new data
     */
    function handleGetFacility() {
      fetchFacility({
        providerId,
        searchParams,
        isDemo,
        navigate,
        setLoading,
        onRateLimited: () => setRateLimited(true),
      })
        .then(({ facility, currentSpecialty }) => {
          if (facility) {
            dispatch({
              type: 'updateFacility',
              facility,
              locationId: searchParams.locationId,
              specialty: currentSpecialty,
              networkId: searchParams.networkId,
            });
          }
        })
        .finally(() => setLoading(false));
    }

    if (providerId.startsWith('p.')) {
      handleGetProfessional();
    } else {
      handleGetFacility();
    }
  }, []);

  return (
    <FixedWidthLayout maxWidth="xl">
      <Box paddingBottom="md">
        <IconButton aria-label="back" onClick={goBack}>
          <ArrowBackIcon color="action" />
        </IconButton>
      </Box>
      <ProviderDetailContext.Provider value={details}>
        <ProviderDetailDispatchContext.Provider value={dispatch}>
          {loading ? (
            <Spinner variant="dark" />
          ) : rateLimited ? (
            <RateLimited />
          ) : (
            <MainContent metricIds={metricIds} searchParams={searchParams} />
          )}
        </ProviderDetailDispatchContext.Provider>
      </ProviderDetailContext.Provider>
    </FixedWidthLayout>
  );
};
